import './DragDropStore.css'
import {stringify, React, _, utils, lockr, ReactDOM} from '@api'
import {GroupByGroupStore} from '@modules'
import {configs, ui, CatalogStore, site, onSiteError, globalStores, CatalogImageService} from '@stores'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {computedFn} from 'mobx-utils'
import {DragDropEventHandlers, DragDropInteraction, IDragDropElement, DD_CENTRIC_TYPE_PREFIX, IDropElement, IDragDropElementState, WindowDragEventHandlers, DragDropInteractions, VerifyDragDropCallback, VerifyDragDropCallbackResult} from './'
import {removeUndefinedsNested, tryParseJson} from '@utils'
import {action, autorun, computed, observable, runInAction, toJS, when} from 'mobx'
import JSZip from 'jszip'
import { DragImagesPlaceholder } from './DragImagesPlaceholder'

export const DEBUG_DRAGDROP = DEBUG && false

export type DragStartOptions = { setDragImage?: boolean, elementToClone?: HTMLElement, onCustomizeDragImageNode?: (div: HTMLDivElement) => void }

export const CatalogDropTargets = {
  root: 'CatalogRoot',
  carousel: 'CatalogItem_Carousel',
  createItemsPanel: 'Catalog_CreateItems'
}

// Future iframe support D&D
// https://github.com/James-E-Adams/iframe-drag-n-drop

/**
 * Application-Wide Handling of Drag and Drop
 *
 * The current "State of the World" for VIP drag and drop is dependant on a multitude of complex dynamic factors such as user role(s), current workspace, grouped or not, etc
 *
 * We precalculate what Noun-to-Noun interactions are currently allowed via {@see ddInteractions}
 *
 * For the allowed interactions on a page we know some rough stuff:
 *    What noun-to-noun concepts are allowed - stuff like creating new items in the catalog by dropping library items on to the canvas, assigning things like tags, fields, materials, etc by dragging them on to catalog items and group headers.
 *
 * Limitations
 *    We cannot get access to the contents of a given drag event's DataTransfer when it comes from an external window, only their TYPES.
 *    We get around this limitation by encoding our data-transfer type names to indicate their type and ID (ex: 'vip-color/C12345')
 *
 */
export class DragDropStore {
  readonly ASSIGN_FIELD = 'assignField'
  readonly ASSIGN_LIBRARY = 'assignLibrary'

  constructor(public catalog: CatalogStore) {
    // console.log(`construct dragdrop - ${catalog.debugStoreIdPrefix}`)
    synchronizeStores(() => {
      // Put the drag drop data attributes on the root body so we can style popovers (in portals)
      autorun(() => {
        const {bodyDataAttributes} = this, {body} = document
        Object.keys(bodyDataAttributes).forEach(attr => {
          const v = bodyDataAttributes[attr]
          v ? body.setAttribute(attr, v) : body.removeAttribute(attr)
        })
      }, {name: `Directly Update body attribute with drag drop state so global CSS can update immediately during drag`})

      autorun(() => {
        const {dragItem, allowDrop, dragOverItem} = this

        if (!dragItem && this.checkedForImageInDataTransfer) {
          this.checkedForImageInDataTransfer = false
        }

        this.updateDropTargets(dragItem, allowDrop, dragOverItem)
      }, {name: `Show/Hide drop placeholder(s) throughout the app after a drag enter event`})

      this.windowDragEventHandlers = new WindowDragEventHandlers()
    })
  }

  handlersFor(def: Partial<IDragDropElement>) {
    const {ddHandlers, catalog} = this,
      existing = ddHandlers.get(def.id)

    if (existing) {
      // todo verify the field are different

      return existing
    }

    const newHandler = new DragDropEventHandlers(catalog, def)

    return newHandler
  }

  @action private updateDropTargets = (dragItem: IDragDropElement, allowDrop: (def: IDragDropElement) => VerifyDragDropCallbackResult, dragOverItem: IDragDropElement) => {
    runInAction(`Update visible drop placeholders across placeholder stores`, () => {
      const {dropPlaceholders, ddHandlers, catalog, catalog: {stores: {es}}} = this
      if (!dragItem || !dragOverItem) {
        this.clearDropPlaceholders()
      } else {
        if (dragOverItem.dragType !== catalog.index) {
          if (dropPlaceholders.has(dragOverItem.id)) return

          const valid = this.dragOverItem && this.dragItem
                          && this.dragItem.id != this.dragOverItem.id

          if (!valid) {
            // if (this.dragItem && this.dragOverItem) {
            //   DEBUG && console.debug(`Invalid drag/drop combo - not an allowed interaction`, this.dragItem, this.dragOverItem)
            // }

            this.clearDropPlaceholders()
          } else {
            this.clearDropPlaceholders()
            dropPlaceholders.add(dragOverItem.id)
          }
        } else {
          this.clearDropPlaceholders()
            // Is this a field assignment?  If so we can drop on selected object(s)
          if (catalog.catalogConfig.fields.some(f => dragItem.dragType == es.indexes.tag || f.esIndex == dragItem.dragType || f.showAsLibrary)) {
            const {selection: {selectedIds}} = catalog
            if (selectedIds.has(dragOverItem.id)) {
              dropPlaceholders.replace(Array.from(selectedIds.keys()).filter(id => allowDrop(ddHandlers.get(id).def)?.allowed))
            } else {
              dropPlaceholders.replace([dragOverItem.id])
            }
          } else {
            dropPlaceholders.replace([dragOverItem.id])
          }
        }
      }

      if (dropPlaceholders.size) {
        Array.from(dropPlaceholders.keys()).forEach(id => {
          const ddh = this.ddHandlers.get(id)
          if (ddh) {
            ddh.showDropIndicator = true
          }
        })
      }

      // if (modifiedDropPlaceholders) {
      //   console.log(`modifiedDropPlaceholders`)
      //   document.querySelectorAll(`.dropIndicator[data-placeholder-drag-over='true']`).forEach(elem => elem.setAttribute('data-placeholder-drag-over', 'false'))
      //
      //   if (dropPlaceholders.size) {
      //     const selector = _.flatMap(Array.from(dropPlaceholders.values()).map(id => {
      //       const idSelector = `.dropIndicator[data-id='${id}']`
      //
      //       if (!catalog.isProductView) {
      //         const parsedId = catalog.parseIdFromPath(id)
      //         return [idSelector, `.dropIndicator[data-catalog-id='${parsedId}']`]
      //       }
      //
      //       return [idSelector]
      //     })).filter(_.identity).join(', ')
      //
      //     document.querySelectorAll(selector).forEach(elem => elem.setAttribute('data-placeholder-drag-over', 'true'))
      //   }
      // }
      //console.log(`Updated drop placeholders: `, Array.from(dropPlaceholders.values()), {dragItem, dragOverItem})
    })
  }

  checkedForImageInDataTransfer = false

  windowDragEventHandlers : WindowDragEventHandlers

  /// region Drag and Drop State

  /**
   *Full path to D&D handler (includes group A . Group B . catalog ID
   */
  ddHandlers = observable.map<string, DragDropEventHandlers>({}, {deep: false})

  /**
   * 1 -> many for catalog item ID in multiple potential groups locations and to deal with moving between groups
   */
  ddHandlersCatalogItem: { [id: string]: Array<DragDropEventHandlers> } = {}

  /**
   * Be careful to only put true/false items in here and not those with dynamic keys or they will never get removed from the body
   */
  @computed get bodyDataAttributes() {
    const {isDragging, catalog} = this
    return {
      'data-is-dragging': isDragging
    }
  }

  /**
   * We are rendered below the body tag in React's render tree so CSS with attributes reliant on React-set values, won't work {@see bodyDataAttributes} for global attributes we set manually.  The ones below are set by React in the global {@see VIP_MerchBoard_App}
   */
  @computed get appDataAttributes() {
    const {catalog} = this
    return {
      ...this.bodyDataAttributes,
      'data-drag-type': this.dragItem?.dragType
      //...Array.from(dropPlaceholders.values()).reduce((acc, id) => ({...acc, [`data-drag-over-${_.toLower(id).replaceAll('_', '-')}`]: 'true'}), {})
    }
  }

  /** What noun <-> noun interactions are currently available in our catalog?
   * Examples:
   *    [Library Item] -> [Catalog Item]                      Assign a MATERIAL to a PRODUCT PLACEHOLDER
   *    [Library Item] -> [Create Catalog Item Panel]         Create a new PRODUCT PLACEHOLDER and assign it this library item I chose (a COLOR or STYLE, etc)
   *    [TAG] -> [Hierarchy Group Header]                     Assign TAG='Important' to the products contained within a given GROUP in the hierarchy
   * etc
   * Depends on things like current workspace, available libraries, etc.
   * Drag external image file on to catalog - create products
   * Drag external image file on to group - create products with attributes preset
   * Drag library item (ListPreviewCard) on to product card to assign style, attribute, tag, etc
   * Drag style to create MPV placeholder
   * Drag product between groups - reassign attributes
   * Drag any of these items from App window A to app window B (library from A on to product card of B, attribute group with product card a -> group b)
   * Drag outside link (Google image search, walmart image, etc) on to group or catalog to create new products
   * Drag image(s) on to existing product card to assign images
   * Drag product card on to card in another group to either replace or swap it
   * Drag carousel items around to reorder/delete

   **/
  @computed.struct get ddInteractions(): { [dragSourceType: string]: { [target: string]: DragDropInteraction } } {
    if (this.catalog.isGlobalStore) {
      // console.trace(`We shouldn't be here - this is a global store`)
      return {}
    }

    const {
        catalog, detailsViewDragDropInteractions, assortmentCardsDragDropInteractions, verifyCatalogItemIsEditableForDragOverOperation, createCatalogItemFromDropImage,
        catalog: {
          stores: {es}, features: {actionButtons},
          index: catalogIndex, catalogConfig, dragLibraries, groupBy, isProductView, isShowingClientPDF, assignableFields, canCreateItem,
          mutations: {
            assignCatalogFieldToCatalogItems, dropOnGroupHeader, assignLibraryItemToCatalogItem, assignImageToCatalogItem
          },
          sidebar: {createCatalogItemFromDragDrop_CreateItems}
        }
      } = this,
      dragFields = _.uniq(assignableFields)

    return isShowingClientPDF ? {} : removeUndefinedsNested(
      _.merge(
        {},
        {
          // Create product(s) from plain text (lines with product names for example)
          'plain-text': actionButtons?.create === false ? undefined : {
            'GroupHeader': {},
            [CatalogDropTargets.createItemsPanel]: {},
            [CatalogDropTargets.root]: {}
          },

          // Create products from images or add images to existing product/style/etc
          'images': /*!catalog.isActionButtonEnabled('uploadImages') ? undefined :*/ {
            'GroupHeader': {onDrop: canCreateItem ? createCatalogItemFromDropImage : undefined},
            [CatalogDropTargets.createItemsPanel]: canCreateItem ? {onDrop: createCatalogItemFromDropImage} : undefined,
            [CatalogDropTargets.root]: {},
            'CatalogItem_Carousel': {},
            ...catalog.imageEditableLibraries.reduce((acc, {index}) => _.assign(acc, {[index]: {onDrop: assignImageToCatalogItem}}), {}),
            [catalogIndex]: {onDrop: assignImageToCatalogItem, operation: 'assign', verify: verifyCatalogItemIsEditableForDragOverOperation}
          }
        },

        // Dynamically associate drag and drop targets
        {
          // Catalog item ->
          [catalogIndex]: {
            // Swap vs Replace item between groups
            // [catalogIndex]: {onDrop: assignImageToCatalogItem, operation: 'assign', verify: verifyCatalogItemIsEditableForDragOverOperation},

            // CatalogItem -> LibraryItem   (Assign)
            //...dragLibraries.reduce((acc, { index, assignTo }) => ({ ...acc, [index]: { onDrop: catalog.mutations.assignLibraryItemToCatalogItem }, field: assignTo }), {}),

            // CatalogItem -> Group (Change attribute values)
            'GroupHeader': groupBy.dragDrop_moveCatalogItemsBetweenGroupsInteraction,

            /** D&D of card image **/
            'desktop': { multiple: true}

            // No dragging of catalog item on to attribute fields
            //...dragFields.reduce((acc, { field }) => ({ ...acc, [field]: { onDrop: catalog.mutations.assignCatalogFieldToCatalogItems, multiple: true } }), {}),

          }

        } as DragDropInteractions,

        // Library items -> Catalog Item, Create Item, Create Item in Group
        dragLibraries.reduce<DragDropInteractions>((acc, {index, assignTo, catalogFields, assignUnique}) => ({
          ...acc,
          [index]: {
            // LibraryItem -> CatalogItem   (Assign)
            [catalogIndex]: {
              onDrop: assignLibraryItemToCatalogItem,
              field: assignTo,
              catalogFields,
              operation: 'assign',
              multiple: catalog.selection?.selectedIds?.size > 0,
              verify: (drag, dragOver) => {
                if (!verifyCatalogItemIsEditableForDragOverOperation(drag, dragOver).allowed) return false
                // If we can only assign the drag item to a single drop item and we have multiple selected items, don't allow drop
                if (assignUnique) {
                  if (catalog.selection && catalog.selection.selectedIds.size > 1 && catalog.selection.selectedIds.has(dragOver?.id)) {
                    return {
                      allowed: false,
                      message: `Must be Unique`
                    }
                  }

                  // const currentValue = _.get(catalog.catalogItemById[dragOver.id], `${assignTo}.id`)
                  // if (currentValue == drag.id) {
                  //   return false
                  // }
                }

                return {allowed: true}
              }
            } as DragDropInteraction,

            // Library Item -> Group (Create new catalog item with library Item assigned + attribute value(s) assigned)
            // For tag / assignable fields this is assign and not create
            'GroupHeader': index == es.indexes.tag ? {multiple: true, field: assignTo, onDrop: dropOnGroupHeader, operation: 'assign'}
                                                   : !canCreateItem ? undefined : {onDrop: createCatalogItemFromDragDrop_CreateItems, multiple: false, operation: 'create', field: assignTo, verify: groupBy.verifyDragLibraryIsDifferentThanDropGroupHeader},

            // Library Item -> Create object with library property already set
            [CatalogDropTargets.createItemsPanel]: !canCreateItem ? undefined : {onDrop: createCatalogItemFromDragDrop_CreateItems, operation: 'create', field: assignTo},
            [index]: _.some(catalogConfig.fields, f => !_.isEmpty(f.refField) && f.esIndex == index) && {
              onDrop: assignLibraryItemToCatalogItem,
              field: assignTo,
              catalogFields,
              operation: 'assign'
            } as DragDropInteraction,
          }
        }), {}),

        dragFields?.reduce<DragDropInteractions>((acc, {field, def}) => ({
          ...acc,
          [field]: def?.ref ? undefined : {
            // Assign attribute to selected items
            [catalogIndex]: {onDrop: assignCatalogFieldToCatalogItems, operation: 'assign', multiple: true, verify: verifyCatalogItemIsEditableForDragOverOperation},
            'GroupHeader': {onDrop: dropOnGroupHeader, field, operation: 'assign', verify: verifyCatalogItemIsEditableForDragOverOperation}
          }
        }), {}),
        detailsViewDragDropInteractions,
        assortmentCardsDragDropInteractions
      ))
  }

  allowedDropInteractionsFor = computedFn((def: IDragDropElement) => {
    const interactions = Object.values(this.ddInteractions[def.dragType] ?? {}).filter(({verify}) => !verify || verify(def, null)?.allowed)
    return interactions
  })

  /// endregion

  /**
   * Hide all visible drop placeholders
   */
  @action clearDropPlaceholders = () => {
    const {dropPlaceholders, ddHandlers} = this
    if (dropPlaceholders.size) {
      const toDelete =Array.from(dropPlaceholders.keys())

      toDelete.forEach(id => ddHandlers.get(id).showDropIndicator = false)
      dropPlaceholders.clear()
    }
  }

  get isGlobalStore() { return this.stores.isGlobalStore}

  @computed get detailsViewDragDropInteractions(): DragDropInteractions {
    if (this.isGlobalStore) return
    const {catalog: {detailViewsForWorkspace, _fieldByName, mutations: {assignLibraryItemToCatalogItem}}} = this,
      droppableSections = _.chain(detailViewsForWorkspace).values().map(dv => _.flatMap(dv?.modal?.tabs?.map(t => t?.sections?.filter(s => s.dropTarget)))).flatten().compact().value(),
      r = droppableSections.reduce<DragDropInteractions>((acc, {dropTarget}) => ({
        ...acc,
        ...(!dropTarget?.field || !_fieldByName[dropTarget.field]?.esIndex
            ? {}
            : {
            [_fieldByName[dropTarget.field].esIndex]: {
              [dropTarget.field]: {
                onDrop: assignLibraryItemToCatalogItem
              }
            }
          })
      }), {})

    return r
  }

  get assortmentCardsDragDropInteractions(): DragDropInteractions {
    if (this.isGlobalStore) return
    const {catalog: {assortments}} = this
    if (!assortments) return {}

    const {onDragDropHeroCardToHeroCard, onDragDropVerifyAllowed} = assortments

    return {
      assortmentHeroCard: {
        assortmentHeroCard: {
          verify: onDragDropVerifyAllowed,
          onDrop: onDragDropHeroCardToHeroCard
        }
      }
    }
  }
  // region Verifications (Conditional logic as to whether drag/drop operation is valid for a specific instance of an item)

  /**
   * This can be something like a tag or library item dragged OVER a product card
   * OR it can be product card dragged over a group header
   *
   */
  verifyCatalogItemIsEditableForDragOverOperation : VerifyDragDropCallback = computedFn((dragItem: IDragDropElement, dragOverItem?: IDragDropElement) : VerifyDragDropCallbackResult => {
    const {catalog} = this,
      catalogItem = dragOverItem?.target === catalog.index ? dragOverItem?.targetExtra?.data : dragItem?.dragType === catalog.index ? (dragItem?.targetExtra?.data ?? dragItem?.targetExtra?.store?.props?.data)  : null,
      {dragType} = dragItem,
      {ASSIGN_FIELD, ASSIGN_LIBRARY} = this

    let r : VerifyDragDropCallbackResult

    if (dragItem.dragType === 'images' && catalogItem) {
      if (!catalog.isActionButtonEnabled('uploadImages')) {
        r = {allowed: false, message: 'Image Upload is not Allowed'}
      }
    }

    if (!r && catalogItem && !catalog.isCatalogItemEditableByUser(catalogItem)) {
      r = {allowed: false, message: `Catalog item isn't editable by this user`}
    }

    if (_.isEmpty(r) && (dragItem.id === 'EXTERNAL' || dragType === 'plain-text' || dragItem.dragType === 'images')) { r = {allowed: true} }
    if(_.isEmpty(r)) {
    switch (dragItem.meta) {
      case ASSIGN_LIBRARY: {
        const {catalog: {libraryByIndex, _fieldByName, workspaceConfig}} = this,
          library = libraryByIndex[dragItem.dragType],
          d = dragItem?.targetExtra?.data ?? catalogItem

        const assignToField = library?.assignTo ?? library?.assignTo2?.field

        if (assignToField) {
          const catalogField = _fieldByName[assignToField]
          if (!r && _.isUndefined(catalogField?.editable) || _.isNull(catalogField?.editable)) { r = {allowed: true} }
          if (!r && _.isBoolean(catalogField?.editable)) { r = {allowed: catalogField.editable, message: `${catalogField.field} isEditable?`} }

          if (_.isFunction(catalogField?.editable) && !_.isEmpty(d)) {
            !r && (r = {allowed: catalogField.editable({data: d, stores: catalog.stores, workspace: workspaceConfig}), message: `${catalogField.field} isEditable? (custom logic)`})
          }
        }
        break
      }

      case ASSIGN_FIELD: {
        r = {allowed: catalog.isFieldEditable(dragItem.target, () => catalogItem), message: `${dragItem.target} is Editable?`}
        break
      }

      default: {
        // console.warn(`Unhandled D&D verifyCatalogItemIsEditable case`, {dragItem, dragOverItem})
        r = {allowed: true}
        break
      }
    }
  }

    !r && (r = {allowed: false, message: `verifyCatalogItemIsEditableForDragOverOperation unhandled case`})

    // console.log(`verifyCatalogItemIsEditableForDragOverOperation`, {r, dragItem, dragOverItem})
    return r
  })

  allowDrop = computedFn((def: IDragDropElement) : VerifyDragDropCallbackResult => {
    const {id, target} = def,
      {dragItem: {dragType}, dragItem, isDragging} = this

    if (!isDragging || !dragType || _.isEmpty(dragItem)) return {allowed: false}

    if (dragType === target && target === 'assortmentHeroCard') return {allowed: true}

    if (dragItem.id == id && dragType == target) {
      console.warn(`Cannot drag/drop an item on itself`)
      return {allowed: false}
    }
    const allowedTargets = this.ddInteractions[dragType]

    if (_.isEmpty(allowedTargets)) {
      //console.warn(`No types are registered as valid drop targets for drag type ${dragItem.dragType}`)
    } else {
      const drop = allowedTargets[target]
      return !drop ? {allowed: false} : drop?.verify ? drop.verify(this.dragItem, def) : {allowed: true}
    }

    return {allowed: false}
  })

  // endregion

  // region Events

  @action createCatalogItemFromDropImage = async (dragItem: IDragDropElement, dropItem: IDragDropElement, context: Partial<DragDropInteraction>, dt: DataTransfer) => {
    const {catalog: {createItem, features: {createOneItemPerImage}, img: {readImageFromDataTransfer}}} = this

    if (dragItem.dragType === 'images') {
      const images = await readImageFromDataTransfer(dt)

      if (createOneItemPerImage) {
        await Promise.all(
          images.map(async image => {
          let item: any = {images: [image], multiple: false}

          if (dropItem.target === 'GroupHeader') {
            const groupStore: GroupByGroupStore = dropItem.targetExtra.store
            await groupStore.createGroupCatalogItem(item)
          } else {
            const drop = tryParseJson(dropItem.id) ?? (_.isString(dropItem.id) ? {} : dropItem.id)
            item = {
              ...item,
              ...drop
            }
            await createItem.createOfflineItem({data: item})
          }
        }))
      }
      else {
        let item: any = {images, multiple: images.length > 1}

        if (dropItem.target === 'GroupHeader') {
          const groupStore: GroupByGroupStore = dropItem.targetExtra.store
          await groupStore.createGroupCatalogItem(item)
        } else {
          const drop = tryParseJson(dropItem.id) ?? (_.isString(dropItem.id) ? {} : dropItem.id)
          item = {
            ...item,
            ...drop
          }
          await createItem.createOfflineItem({data: item})
        }
      }
    } else {
      console.warn(`createFromDropImage called with invalid dragType ${dragItem.dragType}`)
    }
  }

  // endregion

  // region State

  dropPlaceholders = observable.set<string>()
  @observable static _dragItem: IDragDropElement = {}

  @computed get dragItem() {
    return DragDropStore._dragItem ?? {}}


  get stores() { return this.catalog.stores }
  @observable.ref dropItem ?: IDropElement
  @observable.ref private _dragOverItem: IDragDropElement
  dragOverItemElem : EventTarget

  @computed get dragOverItem() : IDragDropElement {
    const {ddHandlers, _dragOverItem} = this
    if (!_dragOverItem) return null

    return ddHandlers.get(_dragOverItem.id)?.def ?? _dragOverItem
  }

  pendingDragOverItemId: string

  @observable dragGroup: string

  /**
   * Is the user currently dragging an item around the page?
   *
   * This can be set by:
   *    Us when the user starts to drag an item registered in our {@see ddInteractions}
   *    The window.'s {@see window_onDragOver}, when an item is dragged from outside the page into the window.
   *      This could be an image, a line of text, a bunch of catalog items that have been multi-selected in another browser tab now being dragged into our window, etc
   *
   */
  @computed get isDragging() { return this.dragItem?.id != null}

  @computed get dragType(): string {
    return this.dragItem?.dragType ?? 'images'
  }

  // @computed get isDragging() {
  //   return this.dragItem.dragType != null
  // }

  //endregion

  // region Event Handling

  /**
   * When dragging between windows, we only have D&D information via the dataTransfer object
   * When dragging, we can only examine what types are available, not the actual values
   * When dropping, we can read out the real value
   * @param dataTransfer
   * @param isDrop
   */
  @action readDataTransfer = (dataTransfer: DataTransfer, isDrop = false) => {
    const {types} = dataTransfer
    const {setDragItem} = this

    DEBUG_DRAGDROP && console.log(`readDataTransfer`, {isDrop, types, length: dataTransfer.files.length, dataTransfer})

    if (!types.length) return

    !isDrop && console.log(`Drag from outside source:, ${types}`)

    const centricType = types.find(t => t.startsWith(DD_CENTRIC_TYPE_PREFIX))

    let dragItem : IDragDropElement

    if (centricType) {
      const slash2Index = centricType.indexOf('/', DD_CENTRIC_TYPE_PREFIX.length)
      const type = centricType.substring(DD_CENTRIC_TYPE_PREFIX.length, slash2Index - DD_CENTRIC_TYPE_PREFIX.length)
      const count = parseInt(centricType.substring(slash2Index + 1))
      try {
        const data = isDrop ? JSON.parse(dataTransfer.getData(centricType)) : {}

        //console.log(type, data, count)
        dragItem = {id: 'EXTERNAL', dragType: type, count, ...data}
      }
      catch (err) {
        console.error(err)
        debugger
        dragItem = {id: 'EXTERNAL', dragType: type, count}
      }
    } else if (types.includes('text/uri-list')) {
      dragItem = {id: isDrop ? dataTransfer.getData('text/uri-list') : 'EXTERNAL', dragType: 'images'}
    } else if (types.includes('text/plain')) {
      dragItem = {id: isDrop ? dataTransfer.getData('text/plain') : 'EXTERNAL', dragType: 'plain-text'}
    } else if (types.includes('Files')) {
      dragItem = {id: isDrop ? 'Files' : 'EXTERNAL', dragType: 'images'}
    }
      // else if (utils.isJson(dt.getData('text/plain'))) {
      //   const dataTransferItem = JSON.parse(dt.getData('text/plain'))
      //   if (dataTransferItem && dataTransferItem.id !== dragItem.id) { setDragItem(e, dataTransferItem) }
    // }
    else {
      console.trace(`Unhanded dataTransfer type from outside window:  ${types}`)
    }

    if (dragItem && !isDrop) {
      setDragItem(null, dragItem)
    }

    return dragItem
    //console.log(toJS(this.dragItem))
  }

  formatAsHtml = (dd: IDragDropElement) => {
    const heightMultiplier = ui.settings.dragDropImageWidth / dd.imageWidth
    //return `<img style="width:${ui.settings.dragDropImageWidth};height:${dd.imageHeight * heightMultiplier};" src="${dd.imageDataUrl}" title="${_.trim(dd.filename)}" >` //<br/><a href="${nav.urlForObject(catalog.catalogItemById[dd.id])}" target="_blank"><span style="font-size:12px;">${_.trim(dd.filename)}</span></a>`

    // In word this breaks the raw img drag to powerpoint but creates nice hyperlinks in google doc :(
    //return `<a href="${nav.urlForObject(catalog.catalogItemById[dd.id])}">&zwnj;<img width="${ui.settings.dragDropImageWidth}" height="${dd.imageHeight * heightMultiplier}" src="${dd.imageDataUrl}" title="${_.trim(dd.filename)}"></a>`
    return `<img width="${ui.settings.dragDropImageWidth}" height="${dd.imageHeight * heightMultiplier}" src="${dd.imageDataUrl}" title="${_.trim(dd.filename)}">`
  }

  dragImageURIsFor = (dragItem : IDragDropElement) => {
    const {ddHandlers} = this
    if (!_.isEmpty(dragItem)) {
      return [dragItem.id, ...(dragItem.extraIds??[])].map(id => ddHandlers.get(id)?.def).filter(def => def?.imageDataUrl) ?? []
    }
    return []
  }

  @computed get dragImageURIs() {
    return this.dragImageURIsFor(this.dragItem)
  }

  @action setDragItem = (e: React.DragEvent, dragItem: IDragDropElement, catalog: CatalogStore = null, doAsync = false) => {
    if (!_.isEmpty(dragItem)) {
      DEBUG_DRAGDROP && dragItem && console.debug(`D&D Start`, {dragItem, store: this.catalog.debugId})

      const {formatAsHtml} = this

      if (catalog) {
        const {index, selection, workspaceConfig, componentStores: {itemsListItem, cards}, parseIdFromPath} = catalog
        if (dragItem.dragType === index) {
          if (selection && selection.selectedIds.has(dragItem.id)) {
            dragItem.extraIds = Array.from(selection.selectedIds.keys()).filter(id => id != dragItem.id)
          } else {
            dragItem.extraIds = []
          }

          dragItem.cardStores = [dragItem.id, ...dragItem.extraIds].map(id => cards[id] ?? itemsListItem[id])
        }
      }

      if ((dragItem.dragType === 'assortmentCard') || (dragItem.dragType === 'assortmentHeroCard')) {

      }

      if (dragItem.id !== 'EXTERNAL') {
        dragItem.count = (dragItem.id == null ? 0 : 1) + (dragItem.extraIds?.length ?? 0)
      }

      if (dragItem.id === 'EXTERNAL' && dragItem.dragType === 'images') {
        dragItem.count = 0 // on drag can not read files length
      }

      if (catalog && e) {
        const {dataTransfer: dt} = e,
          cmdKeyDown = (e.ctrlKey || e.metaKey)

        DEBUG_DRAGDROP && console.log(`start drag - ${dragItem.dragType} - ${dragItem.id}`, {cmdKeyDown, dt})
        //dt.setData(`text/plain`, stringify(dragItem))
        /* dragItem.extraIds?.forEach(id => dt.setData(`${DD_CENTRIC_TYPE_PREFIX}${dragItem.dragType}`, stringify(dragItem)))*/
        //dt.dropEffect = 'copy'

        if (!utils.isSafari) {
          if (dragItem.count === 1) {
            if (dragItem.imageDataUrl) {
              dt.setData(`text/html`, formatAsHtml(dragItem))
            }

            if (dragItem.imageUrl) {
              dt.setData('text/c8-image', dragItem.imageUrl)
              dt.setData('text/plain', dragItem.imageUrl)
              dt.setData('text/uri-list', dragItem.imageUrl)
              //console.log(dragItem.imageUrl)
            }

            //console.log(dragItem.imageUrl)
            if (dragItem.imageDataUrl) {
              const imgUrl = `image/png:${_.trim(dragItem.filename)}.png:${dragItem.imageDataUrl}`
              dt.setData('DownloadURL', imgUrl)
            }
          } else if (dragItem.count > 1) {
            const imageURIs = this.dragImageURIsFor(dragItem)
            if (imageURIs.length) {
              const htmlDragDrop = imageURIs.map(dd => formatAsHtml(dd)).join('\n')
//           const htmlDragDrop = `<meta http-equiv="Content-Type" content="text/html;charset=UTF-8">
// <table style="text-align: center;"><tbody>
// <tr>${imageURIs.map(dd => `<td align="center" style="border: 1px solid black"><img src="${dd.imageDataUrl}" title="${_.trim(dd.filename)}"></td>`)}</tr>
// <tr>${imageURIs.map(dd => `<td align="center"><a href="${nav.urlForObject(catalog.catalogItemById[dd.id])}" target="_blank">${dd.filename}</a></td>`)}</tr>
// </tbody></table>`

              dt.setData(`text/html`, htmlDragDrop)
              dt.setData('text/uri-list', imageURIs.map(d => d.imageUrl).join('\n'))
              dt.setData('DownloadURL', imageURIs.map(dd => `image/png:${_.trim(dd.filename)}.png:${dd.imageDataUrl}`).join('\n'))

              if (cmdKeyDown || imageURIs.length > 1) {
                DEBUG_DRAGDROP && console.debug(`Creating zip file with ${imageURIs.length} images...`)
                const zip = new JSZip()
                const filenameCount = new Map<string, number>()
                imageURIs.forEach(def => {
                  const filename = _.trim(def.filename),
                    count = filenameCount.get(filename)

                  filenameCount.set(filename, count ? count + 1 : 1)
                  zip.file(`${filename}${count ? ` (${count})` : ''}.png`, def.imageDataUrl.replace(/^data:image\/(png|jpg);base64,/, ''), {base64: true})
                })

                const zipped = zip.generate(/*{platform: 'darwin'}*/)

                //
                dt.setData('DownloadURL', `application/zip:${catalog?.workspaceConfig?.title ?? 'VIP Images'}.zip:data:application/zip;base64,${zipped}`)

                DEBUG_DRAGDROP && console.log(`Set DownloadURL on dataTransfer.  ${imageURIs.length} images encoded.`)
              } else {
                DEBUG_DRAGDROP && console.log(`Hold down cmd or ctrl to zip the contents to a draggable file`)
              }
            } else {
              DEBUG_DRAGDROP && console.debug(`Unable to get image data URLs for dragItems - 'ui.settings.dragImagesToDesktop' needs to be true - ${ui.settings.dragImagesToDesktop}`)
            }
          }
        }

        // If we do this in safari it will break image D&D to the desktop, but we need this for dragging between windows
        if (!cmdKeyDown) {
          dt.setData(`${DD_CENTRIC_TYPE_PREFIX}${dragItem.dragType}/${dragItem.count}`, stringify(_.omit(dragItem, ['targetExtra', 'cardStores'])))
        }

        // Hide any showing context menu
        setTimeout(() => catalog.contextMenu.hideContextMenu_DOM(), 50)
        // console.log(dt.items)
      }
    }
    else if (_.isEmpty(dragItem) && !_.isEmpty(DragDropStore._dragItem)) {
      DEBUG_DRAGDROP && dragItem && console.debug(`D&D Stop`, {dragItem, store: this.catalog.debugId})
    }

    if (DragDropStore._dragItem !== dragItem) {
      if (doAsync) {
        setTimeout(() => { // If we immediately assign this in dragStart it'll cause a render pass and immediately cancel drag/drop
          // console.log(`set drag item to `, dragItem)
          DragDropStore._dragItem = dragItem
        }, 0)
      } else {
        // console.debug(`set drag item to `, dragItem)
        DragDropStore._dragItem = dragItem
      }
    }
    //console.trace(`setDragItem()`, e?.dataTransfer, dragItem)
  }

  /**
   * Create a custom drag indicator for the thing being dragged
   * NYI
   */
  @action setDragCustomImage = async () => {
    const {dragItem: {dragType}} = this

    if (false && dragType == 'CatalogItem') {
      // todo - es
      // const {data: { product }} = await stores.apollo.client.query({
      //   query: PRODUCT,
      //   variables: {
      //     where: {
      //       id: dragItem.id
      //     }
      //   }
      // })
      // let newNode                 = document.getElementById('random_product_dragdrop')
      // if (!newNode) {
      //   newNode    = document.body.appendChild(document.createElement('div'))
      //   newNode.id = 'random_product_dragdrop'
      // }
      // ReactDOM.render(
      //   <ApolloProvider client={stores.apollo.client}>
      //     <MuiThemeProvider theme={MainTheme}>
      //       <CustomDragIndicator data={product}/>
      //     </MuiThemeProvider>
      //   </ApolloProvider>, newNode)
      //
      // dataTransfer.setDragImage(newNode, 0, 0)
    }
  }

  // endregion

  @observable dropProduct: Array<any> = []
  @observable queryVariables: any

  @action onChange_ImportProductImageHiddenFile = (event: React.ChangeEvent<HTMLInputElement>, id, content) => {
    this.createProductsDropTarget_onDrop(event.target, id, content)
  }

  @action resetLoader = (loader: boolean = false, message: string = null) => {
    const {catalog: {massCreate}} = this
    massCreate.loading = loader
    massCreate.uploadingMessage = message
  }

  @action
  createProductsDropTarget_onDrop = async (monitor: HTMLInputElement, id: string | null = null, specification: any = {}) => {
    let files: any = monitor.files

    const {catalog: {img: {getImageFromDragImage}, massCreate:{createProductWithImages}, massCreate}} = this

    try {
      if (_.isEmpty(files)) {
        const file = await getImageFromDragImage(monitor)
        if (file) {
          files = [file]
        }
      }

      if (files && files.length) {
        if (!_.isEmpty(specification)) {
          massCreate.groupSpec = specification
        }

        await createProductWithImages(files)
      }
      // if (files.length === 0 && dragItem && (dragItem.id || dragItem.library)) {
      //   const {
      //     id: dragId,
      //     name,
      //     dragType,
      //     library
      //   } = dragItem
      //   console.log({dragItem})
      //   throw new Error('nyi -es')
      /*let where: any = _.cloneDeep(gql.whereClause)

      if (id === 'unassigned') {
        const type = specification.id.split('_')[0]
        if (type) {
          if (type === 'materials' || type === 'hardware') {
            where[gql.mapping.specToProductWhereEvery[type]] = {
              type: {
                name_not: specMappings[type].type
              }
            }
          } else {
            where[gql.mapping.specToProductWhereEvery[type]] = null
          }
        } else {
          return
        }
      } else {
        where[gql.mapping.groupToProductWhere[typeName]] = {id: specification.id}
      }

      switch (dragType) {
        case 'styles':
        case 'colors':
        case 'hardware':
        case 'materials':
          const title = getSingularLibraryDisplayName(libraryTypes, nav.queryParams.list)
          this.resetLoader(true, `Updating ${title}...`)
          await gql.products.assignSpecificationToProducts({
            variables: {
              input: {
                [gql.mapping.specToInputField[dragType]]: dragId
              },
              where
            }
          })
          break;
        case 'tag':
          this.resetLoader(true, `Updating Tag...`)
          await gql.products.assignCustomAttributesToProducts({
            variables: {
              input: {
                definitionName: enums.TAG_DEFINITION_NAME,
                tagList: [dragId]
              },
              where
            }
          })
          break;
        case 'customAttributes':
        case 'customTags':
          const definition: any = caDefByName[name]
          if (definition) {
            this.resetLoader(true, `Updating ${definition.displayName.singular}...`)
            await gql.products.assignCustomAttributesToProducts({
              variables: {
                input: {
                  definitionName:                                definition.name,
                  [CUSTOM_ATTRIBUTE_VALUE_MAP[definition.type]]: definition.type === 'TagList' ? [dragId] : dragId
                },
                where
              }
            })
          }
          break;
      }*/
      //}
    } catch (err) {
      onSiteError(err)
    } finally {
      this.resetLoader()
    }
  }

  @observable isDropping = false
  @action reset = (forceReset = true) => {
    try {
      DEBUG_DRAGDROP && console.groupEnd()
      if (forceReset || !this.isDropping) {
        this.clearDropPlaceholders()
        this.pendingDragOverItemId = null
        this.setDragOverItem(null)
        this.setDragItem(null, {}, null, true)
        this.queryVariables = null
        this.dropItem = null

        setTimeout(() => {
          this.dragItemClonedNode?.remove()
          this.dragItemClonedNode = null
        }, 50)
      }
    } catch (err) {
      console.error(err)
    }
  }

  @computed get applyDragToSelectedItems() {
    const {dragItem, dragOverItem, catalog} = this, {index, selection: {selectedIds}, stores: {es}} = catalog
    return (dragItem && dragOverItem && dragOverItem.target === index && selectedIds.has(dragOverItem.id) && catalog.catalogConfig.fields.some(f => f.esIndex == dragItem.dragType || dragItem.dragType == es.indexes.tag || f.showAsLibrary))
  }

  @action applyOperation = (ids: string[] | string, nextState: 'loading' | 'success' | 'failed') => {
    if (!_.isArray(ids)) {
      ids = [ids]
    }

    DEBUG_DRAGDROP && console.log(`applyOperation`, ids, nextState)
    const {ddHandlersCatalogItem, catalog} = this
    ids.filter(_.identity).map(id => {
      let handled = false
      if (!catalog.isProductView) {
        const catalogItemId = catalog.parseIdFromPath(id)
        if (catalogItemId != id && ddHandlersCatalogItem[catalogItemId]) {
          handled = true

          ddHandlersCatalogItem[catalogItemId].forEach(({state}) => {
            state.status = nextState
          })
        }
      }

      if (!handled) {
        handled = true
        const {state} = this.ddHandlers.get(id) ?? {}
        if (state) {
          state.status = nextState
        }
        else {
          debugger
        }
      }
    })
  }

  _handleDrop = async (e: React.DragEvent, def: IDragDropElement) => {
    e.preventDefault()
    e.stopPropagation()

    const { catalog, allowDrop, readDataTransfer, dragOverItem, ddInteractions, applyDragToSelectedItems} = this,
      {groupBy, selection: { selectedIds } } = catalog,
      {target} = def

    let dragItem = this.dragItem // Will change if drop from outside window because only then can we read the dataTransfer

    DEBUG_DRAGDROP && console.debug(`Dropping ${dragItem.dragType}-${dragItem.id} on to ${target}-${def.id}`)

    //console.debug(`onDrop`, toJS(def), toJS(dragItem))
    /**
     * Possible drop candidates are:
     *  Create Product
     *  Create Product (within a group)
     *  Image dropped on a product card
     *  Library Item (ListPreviewCard) dropped on a Product Card
     *  Product card dropped in a group (move between groups/reassign attributes)
     *
     */
    try {
      // We may have dragged across window boundaries in which case dragItem will not be set and we will need to pull the data from data transfer (not available until dropped)
      if (dragItem.id === 'EXTERNAL') {
        // console.log('id', id, e.dataTransfer.getData('text/plain'))
        dragItem = readDataTransfer(e.dataTransfer, true)
      }

      // dragItem can change above from reading dataTransfer - before
      const {applyOperation} = this, {dragType} = dragItem

      const doDrop = async (def: IDragDropElement) => {
        //console.trace(`doDrop`, def)
        const allow = allowDrop(def)

        if (!allow.allowed) {
          DEBUG_DRAGDROP && console.log(`Ignoring drop as not allowed from ${dragType} -> ${target}`, allow.message)
          return
        }

        const handler = this.ddHandlers.get(def.id),
          {state} = handler,
          idsToUpdate = _.uniq([def.id, ...(dragItem.extraIds ?? [])])

        applyOperation(idsToUpdate, 'loading')

        if (!ddInteractions[dragType] || !ddInteractions[dragType][target]) {
          DEBUG_DRAGDROP && console.warn(`Invalid drag -> drop  ${dragType} -> ${target}`)
          return
        }

        const {onDrop, ...dropInteraction} = ddInteractions[dragType][target]

        const ids = dragItem.dragType === catalog.index ? [dragItem.id] : !dropInteraction?.multiple || dropInteraction?.operation == 'create' ? [dragOverItem?.id] : applyDragToSelectedItems ? Array.from(catalog.selection.selectedIds.keys()) : (dragOverItem?.targetExtra?.ids ?? [dragOverItem?.id].filter(_.identity))

        // if (dragType === 'images') {
        //   console.log(`dropped an image on ${target} id ${def.id}`)
        // }
        if (applyDragToSelectedItems) {
          applyOperation(ids, 'loading')
        }

        try {
          const r = onDrop && await onDrop(dragItem, def, dropInteraction, e.dataTransfer)

          //console.log(r)

          if (r !== false && !(_.isArray(r) && r.some(v => v === false))) {
            /**Will remove this once we have actually create/update calls in place */
            applyOperation(idsToUpdate,'success')
            if(dragItem.dragType != def.dragType){
              applyOperation(ids, 'success')
            }

            setTimeout(() => {
              applyOperation(idsToUpdate, null)
              applyOperation(ids, null)
            }, ui.settings.updateIndicatorMs)
          } else {
            state.status = null
          }
        } catch (err) {
          onSiteError(err)
          applyOperation(idsToUpdate, 'failed')
          setTimeout(action(`Clear load indicator for ${def.id}`, () => {
            applyOperation(ids, null)
          }), ui.settings.updateIndicatorMs)
        } finally {
          groupBy.queryGroupByCountsForMenu()
        }
      }

      await runInAction(`Apply drop operation`, async () => {
        //console.log(`Dropping ${dragType}-${dragItem.id} on ${target}-${def.id}`)

        if (applyDragToSelectedItems) {
          // Apply the drop to all selected items
          // Apply drop for first selected in case of images
          if (dragType === 'images') {
            await doDrop({...def, id: Array.from(selectedIds.keys())[0]})
          } else if (dragItem.dragType === catalog.index) {
            await Promise.all(Array.from(selectedIds.keys()).map(id => doDrop({...def, id})))
          } else {
            await doDrop(def)
          }
        } else {
          await doDrop(def)
        }
      })
    } finally {
      this.reset(true)
    }
  }

  // This is too expensive, recalculating on every sidebar change causes all drop placeholders to recalc

  // @computed get sidebarDragDropInteractions(): any {
  //   const {sidebar: {left, right}, catalog: {detailViews, _fieldByName, mutations: {assignLibraryItemToCatalogItem}}} = stores
  //   return [left.detailsStore, right.detailsStore]
  //     .filter(_.identity)
  //     .reduce((acc, {selectedDetailTab}) => ({
  //     ...acc,
  //     ...(!selectedDetailTab?.sections?.length
  //         ? {}
  //         : selectedDetailTab.sections.reduce(
  //         (acc, section) =>
  //           ({
  //             ...acc, ...(!section.dropTarget?.field || !_fieldByName[section.dropTarget.field]?.esIndex
  //                         ? {}
  //                         : {
  //                 [_fieldByName[section.dropTarget.field].esIndex]: {
  //                   [section.dropTarget.field]: {
  //                     onDrop: assignLibraryItemToCatalogItem
  //                   }
  //                 }
  //               })
  //           }), {}))
  //   }), {})
  // }

  @observable.ref dragItemClonedNode: HTMLElement

  updateDragImage = (e: React.DragEvent) => {
    const {dragItemClonedNode : clone} = this
    DEBUG_DRAGDROP && console.debug(`Setting drag image`, e, clone)

    if (clone) {
      const {width, height, left, top} = clone.getBoundingClientRect()
      DEBUG_DRAGDROP && console.log(`DD clone dimensions:`, {width, height, left, top})
      e.dataTransfer.setDragImage(clone, width / 2, height / 2)
    }

  }

  DragDropItem_onDragStart = (e: React.DragEvent, def: IDragDropElement, catalog: CatalogStore, {setDragImage, elementToClone, onCustomizeDragImageNode}: DragStartOptions = {setDragImage: true}) => {
    DEBUG_DRAGDROP && console.groupCollapsed(`Drag start - ${def.dragType} - ${def.id}`, {def, setDragImage})
    const {currentTarget, dataTransfer} = e

    //e.preventDefault() // Will prevent any drag
    e.stopPropagation()

    dataTransfer.effectAllowed = 'move'
    dataTransfer.dropEffect = 'move'

    globalStores.dragDrop.setDragItem(e, def, catalog)

    const {dragImageURIs: imageURIs} = this

    DEBUG_DRAGDROP && console.log({dataTransfer, imageURIs, setDragImage})

    if (setDragImage) {
      // Clone the object and set known attributes then use the clone as a drag image

      let clone : HTMLElement,
        domRect: DOMRect

      if (imageURIs.length > 1) {
        clone = document.createElement('div')
        ReactDOM.render(<DragImagesPlaceholder/>, clone)
      }
      else {
        const element = (elementToClone ?? (currentTarget as HTMLElement))
        // domRect = element.getBoundingClientRect()
        clone = element.cloneNode(true) as HTMLElement
        // This is super expensive and delays the start of drag, instead set width/height in the css for individual drag placeholders
        // const {width: maxWidth, height: maxHeight} = currentTarget.getBoundingClientRect()
        // clone.style.setProperty('max-width', `${maxWidth}px`)
        // clone.style.setProperty('max-height', `${maxHeight}px`)
      }

      const //parentNode = (currentTarget as HTMLElement).closest(`[data-type='CatalogRoot']`),
        div = document.createElement('div')

      div.appendChild(clone)
      document.body.appendChild(div)

      clone.setAttribute('data-is-drag-placeholder', 'true')
      div.style.setProperty('position', 'absolute')
      div.style.setProperty('display', 'inline-block')
      div.style.setProperty('top', DEBUG_DRAGDROP ? '50%' : '250%')
      /*div.style.setProperty('left', '0')*/
      div.style.setProperty('z-index', '1')
      // div.style.setProperty('contain', 'layout style size')
      //
      onCustomizeDragImageNode && onCustomizeDragImageNode(div)

      // domRect && div.style.setProperty('max-width', `${domRect.width}px`)
      // domRect && div.style.setProperty('max-height', `${domRect.height}px`)
      this.dragItemClonedNode = div

      this.updateDragImage(e)

      // clone.remove()
    }
  }


  effectAllowedFor = ({target}: IDragDropElement) : "none" | "copy" | "link" | "move" => {
    const {dragItem} = this

    switch (target) {
      case 'Catalog_CreateItems':
        return 'move'
      case 'GroupHeader':
        console.log(dragItem)
        break

      default:
        return 'move'
    }

    return 'move'
  }

  /**
   * YFTS-7715 Don't show intermediate drag over placeholders when the user sweeps
   */
  setDragOverItem_debounce = _.debounce((def: IDragDropElement, elem: EventTarget = null) => {
    this.setDragOverItem(def, elem)
  }, 40, {leading: true, trailing: true})

  @action setDragOverItem = (def: IDragDropElement, elem: EventTarget = null) => {
    // DEBUG && console.log(`setDragOverItem - ${!def ? 'null' : `${def?.target} - ${def?.id}`}`, {def, elem})
    this._dragOverItem = def
    this.pendingDragOverItemId = null
    this.dragOverItemElem = elem
  }

  ddHandlerFor({data, index}: {data: any; index: string}) {
    return new DragDropEventHandlers(this.catalog,{id: this.catalog.idFor(data), dragType: index, target: index, label: data.title, meta: `ddHandlerFor(index)`})
  }
}



// WEBPACK FOOTER //
// ./src/stores/dragDrop/DragDropStore.tsx